/**
 * Converts a percentage (a number between 0 and 1) to a value according to given
 * minValue and maxValue. If percentage exceeds the limits the returned value will be
 * greater or less than max or min values. If you want to avoid this behaviour use
 * `getNormalizedValueFromPercentage` instead
 * @param {number} percentage - The percentage to get value, between 0 and 1
 * @param {number?} maxValue - The maximum possible value, default to 1
 * @param {number?} minValue - The minimum possible value, default to 0
 * @returns {number} The value that the percentage represents
 */
function getValueFromPercentage(percentage) {
  var maxValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
  var minValue = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  return percentage * (maxValue - minValue) + minValue;
}

/**
 * Converts a percentage (a number between 0 and 1) to a value according to given
 * minValue and maxValue. If percentage exceeds the limits it will be normalized
 * before a value is returned. This implies returned value always will be between minValue
 * and maxValue
 * @param {number} percentage - The percentage to get value, between 0 and 1
 * @param {number?} maxValue - The maximum possible value, default to 1
 * @param {number?} minValue - The minimum possible value, default to 0
 * @returns {number} The normalized value that the percentage represents
 */
function getNormalizedValueFromPercentage(percentage) {
  var maxValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
  var minValue = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  var normalizedPercentage = Math.min(1, Math.max(0, percentage));
  return getValueFromPercentage(normalizedPercentage, maxValue, minValue);
}

/**
 * Converts a value to a percentage (a number between 0 and 1) according to given
 * minValue and maxValue. If value is greater than maxValue or less than minValue
 * the returned percentage will be greater than 1 or less than 0, respectively.
 * If you want to avoid this behaviour use `getNormalizedPercentageFromValue` instead.
 * @param {number} value - The value to be converted to percentage
 * @param {number?} maxValue - The maximum allowed value
 * @param {number?} minValue - The minimum allowed value
 * @returns {number} The percentage that the value represents
 */
function getPercentageFromValue(value) {
  var maxValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
  var minValue = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  return maxValue ? (value - minValue) / (maxValue - minValue) : 0;
}

/**
 * Converts a value to a percentage (a number between 0 and 1) according to given
 * minValue and maxValue. If value is greater than maxValue or less than minValue
 * the returned percentage will be normalized before return. It implies returned
 * percentage always will be between 0 and 1.
 * @param {number} value - The value to be converted to percentage
 * @param {number?} maxValue - The maximum allowed value
 * @param {number?} minValue - The minimum allowed value
 * @returns {number} The normalized percentage that the value represents
 */
function getNormalizedPercentageFromValue(value) {
  var maxValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
  var minValue = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  var percentage = getPercentageFromValue(value, maxValue, minValue);
  return Math.max(0, Math.min(1, percentage));
}

/**
 * Converts a percentage (a number between 0 and 1) to its equivalent CSS property value
 * @param {number} percentage - The percentage to be converted to CSS value
 */
function percentageToCSSValue(percentage) {
  return "".concat(percentage * 100, "%");
}

export { getNormalizedPercentageFromValue, getNormalizedValueFromPercentage, getPercentageFromValue, getValueFromPercentage, percentageToCSSValue };
//# sourceMappingURL=index.js.map
